/**
 * O rascunho no modal: lê, grava só o que mudou e resolve o conflito com o chat.
 *
 * POR QUE NÃO O `useDraft` DO ZTNA (spec 2026-10-09 §5): aquele perde a última
 * edição ao desmontar (o debounce morre com o componente) e "Cancelar" apaga o
 * rascunho. Aqui:
 * - a edição fica numa fila (`pendentes`) e sai num PUT só, depois de uma pausa;
 * - fechar o modal ESPERA a fila sair (`fechar`), e desmontar com fila manda o
 *   PUT do mesmo jeito — a última edição nunca fica só na memória da aba;
 * - o chat grava pela mesma porta: a query vive sob a chave-raiz do pedido de
 *   Terraform, que o `message.created` do chat invalida (`useChatRealtime`),
 *   então a gravação do @Atendente faz o modal reler;
 * - quem gravou com versão velha recebe 409 com a diferença, e a tela pergunta
 *   "manter o meu" ou "usar o novo" — nada é sobrescrito em silêncio.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { CHAVE_PEDIDO_TERRAFORM } from '@/hooks/useTerraformChangeRequest'
import {
  gravarRascunho, lerRascunho, trocarAmbienteDoRascunho, valorGravado,
  type ConflitoDoFormulario, type GravacaoDoFormulario, type RascunhoDoFormulario,
} from '@/api/rascunhoDeAmbiente'

export const PAUSA_ANTES_DE_GRAVAR_MS = 700

export type EstadoDaGravacao = 'salvo' | 'pendente' | 'salvando' | 'erro'

export interface Fila {
  campos: Record<string, unknown>
  acompanhantes: number[] | null
  voltar: string[]
}

const filaVazia = (): Fila => ({ campos: {}, acompanhantes: null, voltar: [] })
const vazia = (f: Fila) =>
  Object.keys(f.campos).length === 0 && f.acompanhantes === null && f.voltar.length === 0

export const chaveDoFormulario = (tenantId: number, rascunhoId: number) =>
  [CHAVE_PEDIDO_TERRAFORM, 'formulario', tenantId, rascunhoId] as const

export interface UseRascunho {
  rascunho: RascunhoDoFormulario | undefined
  carregando: boolean
  erroDeLeitura: Error | null
  estado: EstadoDaGravacao
  mensagemDeErro: string | null
  conflito: ConflitoDoFormulario | null
  /** O valor que a tela mostra: a edição na fila, senão o gravado. */
  valor: (caminho: string) => unknown
  /** O campo tem edição na fila (ainda não gravada)? */
  naFila: (caminho: string) => boolean
  editar: (caminho: string, valor: unknown) => void
  voltarAProposta: (caminho: string) => void
  definirAcompanhantes: (ids: number[]) => void
  /** Manda a fila agora; resolve quando ela saiu (ou falhou). */
  salvar: () => Promise<void>
  manterOMeu: () => Promise<void>
  usarONovo: () => void
  /**
   * Troca o ambiente — e com ele o tenant e a conta (consoleai#25804). Salva a
   * fila antes. `null` quando trocou; senão, a frase do porquê (o caminho, se a
   * conta do ambiente não existe).
   */
  trocarAmbiente: (ambiente: string) => Promise<string | null>
}

export function useRascunho(tenantId: number, rascunhoId: number, aberto: boolean): UseRascunho {
  const qc = useQueryClient()
  const chave = chaveDoFormulario(tenantId, rascunhoId)
  const query = useQuery({
    queryKey: chave,
    queryFn: () => lerRascunho(tenantId, rascunhoId),
    enabled: aberto,
    retry: false,
  })
  const filaRef = useRef<Fila>(filaVazia())
  const [fila, setFila] = useState<Fila>(filaVazia())
  const [estado, setEstado] = useState<EstadoDaGravacao>('salvo')
  const [mensagemDeErro, setMensagemDeErro] = useState<string | null>(null)
  const [conflito, setConflito] = useState<ConflitoDoFormulario | null>(null)
  const conflitoRef = useRef<{ fila: Fila; versao: string } | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const emVooRef = useRef<Promise<void> | null>(null)
  const versaoRef = useRef<string | null>(null)
  // O tenant DO RASCUNHO, que muda quando o ambiente é trocado: as gravações
  // vão para ele, não para o tenant com que o cartão abriu o modal.
  const tenantRef = useRef<number>(tenantId)
  // `enviar` precisa reagendar (409 sem disputa) e `agendar` depende dele.
  const agendarRef = useRef<() => void>(() => {})

  // A versão contra a qual a fila vai: a última lida SEM edição na fila. Com
  // edição na fila, uma releitura (o chat gravou) não a troca — senão o PUT
  // passaria por cima do que o chat acabou de gravar.
  useEffect(() => {
    if (query.data) tenantRef.current = query.data.tenant.id
    if (query.data && vazia(filaRef.current) && !conflitoRef.current) {
      versaoRef.current = query.data.versao
    }
  }, [query.data])

  const publicar = (f: Fila) => {
    filaRef.current = f
    setFila(f)
  }

  const enviar = useCallback(async (): Promise<void> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (emVooRef.current) await emVooRef.current
    const lote = filaRef.current
    const versao = versaoRef.current
    if (vazia(lote) || versao === null || conflitoRef.current) return
    publicar(filaVazia())
    setEstado('salvando')
    const corpo: GravacaoDoFormulario = { versao, campos_alterados: lote.campos }
    if (lote.acompanhantes !== null) corpo.acompanhantes = lote.acompanhantes
    if (lote.voltar.length) corpo.voltar_a_proposta = lote.voltar
    const voo = (async () => {
      try {
        const novo = await gravarRascunho(tenantRef.current, rascunhoId, corpo)
        versaoRef.current = novo.versao
        tenantRef.current = novo.tenant.id
        qc.setQueryData(chave, novo)
        setEstado(vazia(filaRef.current) ? 'salvo' : 'pendente')
        setMensagemDeErro(null)
      } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined
        const detalhe = axios.isAxiosError(err) ? err.response?.data?.detail : undefined
        // A fila deste lote volta para a frente da fila atual: nada se perde.
        const devolvida: Fila = {
          campos: { ...lote.campos, ...filaRef.current.campos },
          acompanhantes: filaRef.current.acompanhantes ?? lote.acompanhantes,
          voltar: [...lote.voltar, ...filaRef.current.voltar],
        }
        if (status === 409 && detalhe?.codigo === 'versao_divergente') {
          const c = detalhe as ConflitoDoFormulario
          if (Object.keys(c.campos ?? {}).length === 0) {
            // O chat gravou OUTRO campo: não há disputa, só versão nova.
            versaoRef.current = c.versao
            publicar(devolvida)
            void qc.invalidateQueries({ queryKey: chave })
            // Sem isto a fila ficava "pendente" até a próxima edição.
            agendarRef.current()
            return
          }
          conflitoRef.current = { fila: devolvida, versao: c.versao }
          publicar(devolvida)
          setConflito(c)
          setEstado('erro')
          setMensagemDeErro('Alguém gravou estes campos enquanto você editava.')
          void qc.invalidateQueries({ queryKey: chave })
          return
        }
        publicar(devolvida)
        setEstado('erro')
        setMensagemDeErro(
          (typeof detalhe === 'object' && detalhe?.motivo) || 'Não consegui salvar agora.',
        )
      }
    })()
    emVooRef.current = voo
    try {
      await voo
    } finally {
      if (emVooRef.current === voo) emVooRef.current = null
    }
  }, [tenantId, rascunhoId, qc]) // eslint-disable-line react-hooks/exhaustive-deps

  const agendar = useCallback(() => {
    setEstado('pendente')
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => { void enviar() }, PAUSA_ANTES_DE_GRAVAR_MS)
  }, [enviar])
  agendarRef.current = agendar

  // Desmontar com fila: o PUT sai mesmo assim (a última edição não se perde).
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (!vazia(filaRef.current) && !conflitoRef.current) void enviar()
  }, [enviar])

  const editar = useCallback((caminho: string, valor: unknown) => {
    const f = filaRef.current
    publicar({ ...f, campos: { ...f.campos, [caminho]: valor },
               voltar: f.voltar.filter((c) => c !== caminho) })
    if (!conflitoRef.current) agendar()
  }, [agendar])

  const voltarAProposta = useCallback((caminho: string) => {
    const f = filaRef.current
    const { [caminho]: _fora, ...campos } = f.campos
    publicar({ ...f, campos, voltar: [...f.voltar.filter((c) => c !== caminho), caminho] })
    if (!conflitoRef.current) agendar()
  }, [agendar])

  const definirAcompanhantes = useCallback((ids: number[]) => {
    publicar({ ...filaRef.current, acompanhantes: ids })
    if (!conflitoRef.current) agendar()
  }, [agendar])

  const manterOMeu = useCallback(async () => {
    const c = conflitoRef.current
    if (!c) return
    conflitoRef.current = null
    versaoRef.current = c.versao
    setConflito(null)
    setMensagemDeErro(null)
    await enviar()
  }, [enviar])

  const usarONovo = useCallback(() => {
    const c = conflitoRef.current
    if (!c) return
    const emDisputa = new Set(Object.keys(conflito?.campos ?? {}))
    const campos = Object.fromEntries(
      Object.entries(filaRef.current.campos).filter(([k]) => !emDisputa.has(k)),
    )
    conflitoRef.current = null
    versaoRef.current = c.versao
    publicar({ ...filaRef.current, campos })
    setConflito(null)
    setMensagemDeErro(null)
    void qc.invalidateQueries({ queryKey: chave })
    if (!vazia(filaRef.current)) agendar()
    else setEstado('salvo')
  }, [conflito, agendar, qc]) // eslint-disable-line react-hooks/exhaustive-deps

  const trocarAmbiente = useCallback(async (ambiente: string): Promise<string | null> => {
    await enviar()
    if (conflitoRef.current || !vazia(filaRef.current)) {
      return 'Há uma alteração sua ainda não salva: resolva-a antes de trocar o ambiente.'
    }
    const versao = versaoRef.current
    if (versao === null) return 'O rascunho ainda não foi lido.'
    setEstado('salvando')
    try {
      const novo = await trocarAmbienteDoRascunho(tenantRef.current, rascunhoId, { versao, ambiente })
      versaoRef.current = novo.versao
      tenantRef.current = novo.tenant.id
      qc.setQueryData(chave, novo)
      setMensagemDeErro(null)
      return null
    } catch (err) {
      const detalhe = axios.isAxiosError(err) ? err.response?.data?.detail : undefined
      if (detalhe?.codigo === 'versao_divergente') void qc.invalidateQueries({ queryKey: chave })
      return (typeof detalhe === 'object' && detalhe?.motivo) || 'Não consegui trocar o ambiente agora.'
    } finally {
      setEstado('salvo')
    }
  }, [enviar, rascunhoId, qc]) // eslint-disable-line react-hooks/exhaustive-deps

  const valor = useCallback((caminho: string) => {
    if (caminho === 'acompanhantes' && fila.acompanhantes !== null) return fila.acompanhantes
    return caminho in fila.campos ? fila.campos[caminho] : valorGravado(query.data?.campos, caminho)
  }, [fila, query.data])

  const naFila = useCallback((caminho: string) => (
    caminho === 'acompanhantes' ? fila.acompanhantes !== null : caminho in fila.campos
  ), [fila])

  return {
    rascunho: query.data,
    carregando: query.isLoading,
    erroDeLeitura: (query.error as Error | null) ?? null,
    estado,
    mensagemDeErro,
    conflito,
    valor,
    naFila,
    editar,
    voltarAProposta,
    definirAcompanhantes,
    salvar: enviar,
    manterOMeu,
    usarONovo,
    trocarAmbiente,
  }
}
